/**
 * Shared helpers for reading and writing Formie field inputs from custom markup that
 * lives inside a Formie HTML field.
 */

/**
 * Finds a Formie input by field handle.
 *
 * @param {HTMLFormElement} form
 * @param {string} handle
 * @returns {HTMLElement|null}
 */
export const findField = (form, handle) => {
    const wrapper = form.querySelector(`[data-field-handle="${handle}"]`)

    return (
        wrapper?.querySelector('input, textarea, select') ??
        form.querySelector(`[name="fields[${handle}]"]`) ??
        form.querySelector(`[name$="[${handle}]"]`)
    )
}

/**
 * Reads the current value of a Formie field.
 *
 * @param {HTMLFormElement} form
 * @param {string} handle
 * @returns {string|null}
 */
export const readField = (form, handle) => {
    const input = findField(form, handle)

    if (!input) {
        return null
    }

    const type = (input.getAttribute('type') ?? '').toLowerCase()

    if (type === 'radio' || type === 'checkbox') {
        const name = input.getAttribute('name')
        const scope = form.querySelector(`[data-field-handle="${handle}"]`) ?? form

        const checked = name
            ? form.querySelector(`[name="${CSS.escape(name)}"]:checked`)
            : scope.querySelector(':checked')

        return checked ? (checked.getAttribute('value') ?? checked.value) : null
    }

    return input.value
}

/**
 * Writes a value to a Formie field and tells Formie about it.
 *
 * @param {HTMLFormElement} form
 * @param {string} handle
 * @param {string} value
 * @param {string} source Label used in the console warning, so failures name the caller.
 * @returns {boolean} Whether the field was found.
 */
export const writeField = (form, handle, value, source = 'formieFields') => {
    const input = findField(form, handle)

    if (!input) {
        console.error(
            `[${source}] No "${handle}" field found in the form — the value cannot be set.`
        )

        return false
    }

    if (input.value === value) {
        return true
    }

    input.value = value

    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))

    return true
}
